import type { StateAction } from "@entrilia/shared/types";
import type { InvestorCommunication } from "../../../../api/types/investorCommunicationsTypes";
import type {
  InvestorCommunicationEditState,
  InvestorCommunicationTypeEditModule,
} from "./investorCommunicationEditState";

export interface VideoEditState {
  type: "Video";
  videoFile?: File;
  uploadedVideoFileId?: string;
  isVideoRemoved: boolean;
  thumbnailFile?: File;
  isThumbnailRemoved: boolean;
  allowDownload: boolean;
}

export interface VideoValidationErrors {
  video?: string;
}

const videoRequiredError = "Video file is required";
const videoNoLongerAvailableError = "The uploaded video is no longer available";

const getSavedAllowDownload = (savedCommunication: InvestorCommunication): boolean =>
  savedCommunication.videoContent?.allowDownload ?? false;

const hasSavedVideo = (savedCommunication: InvestorCommunication): boolean =>
  savedCommunication.videoContent?.videoFileId !== undefined;

const hasSavedThumbnail = (savedCommunication: InvestorCommunication): boolean =>
  savedCommunication.videoContent?.thumbnailFileId !== undefined;

const hasVideo = ({ videoFile, isVideoRemoved }: VideoEditState, savedCommunication: InvestorCommunication): boolean =>
  videoFile !== undefined || (!isVideoRemoved && hasSavedVideo(savedCommunication));

export const isVideoRemovalPending = (
  { isVideoRemoved }: VideoEditState,
  savedCommunication: InvestorCommunication
): boolean => isVideoRemoved && hasSavedVideo(savedCommunication);

export const isThumbnailRemovalPending = (
  { isThumbnailRemoved }: VideoEditState,
  savedCommunication: InvestorCommunication
): boolean => isThumbnailRemoved && hasSavedThumbnail(savedCommunication);

// A removed thumbnail is replaced by a frame of the video on save; only a video-less communication deletes it.
export const isThumbnailDeletionPending = (state: VideoEditState, savedCommunication: InvestorCommunication): boolean =>
  isThumbnailRemovalPending(state, savedCommunication) && !hasVideo(state, savedCommunication);

export const needsGeneratedThumbnail = (
  state: VideoEditState,
  savedCommunication: InvestorCommunication,
  communication: InvestorCommunication
): boolean =>
  state.thumbnailFile === undefined &&
  communication.videoContent?.videoFileId !== undefined &&
  (communication.videoContent.thumbnailFileId === undefined || isThumbnailRemovalPending(state, savedCommunication));

export const videoEditModule: InvestorCommunicationTypeEditModule<VideoEditState> = {
  getInitialState: (savedCommunication) => ({
    type: "Video",
    isVideoRemoved: false,
    isThumbnailRemoved: false,
    allowDownload: getSavedAllowDownload(savedCommunication),
  }),
  isContentChanged: (state, savedCommunication) =>
    state.videoFile !== undefined ||
    state.thumbnailFile !== undefined ||
    isVideoRemovalPending(state, savedCommunication) ||
    isThumbnailRemovalPending(state, savedCommunication) ||
    state.allowDownload !== getSavedAllowDownload(savedCommunication),
  isContentCompleted: (state, savedCommunication) =>
    hasVideo(state, savedCommunication) &&
    (state.videoFile !== undefined || state.thumbnailFile !== undefined || hasSavedThumbnail(savedCommunication)),
  validate: (state, savedCommunication, isCompletionRequired) => ({
    video: isCompletionRequired && !hasVideo(state, savedCommunication) ? videoRequiredError : undefined,
  }),
  getContentUpdate: ({ allowDownload }, savedCommunication) =>
    allowDownload === getSavedAllowDownload(savedCommunication) ? undefined : { videoContent: { allowDownload } },
  getPartiallySavedState: (state, savedCommunication, previousSavedCommunication) => {
    const previousVideoContent = previousSavedCommunication.videoContent;
    const isVideoApplied = savedCommunication.videoContent?.videoFileId !== previousVideoContent?.videoFileId;
    const isThumbnailApplied =
      savedCommunication.videoContent?.thumbnailFileId !== previousVideoContent?.thumbnailFileId;

    return {
      type: "Video",
      allowDownload: getSavedAllowDownload(savedCommunication),
      videoFile: isVideoApplied ? undefined : state.videoFile,
      uploadedVideoFileId: isVideoApplied ? undefined : state.uploadedVideoFileId,
      isVideoRemoved: isVideoApplied ? false : state.isVideoRemoved,
      thumbnailFile: isThumbnailApplied ? undefined : state.thumbnailFile,
      isThumbnailRemoved: isThumbnailApplied ? false : state.isThumbnailRemoved,
    };
  },
};

const updateVideoState =
  (update: (state: VideoEditState) => VideoEditState): StateAction<InvestorCommunicationEditState> =>
  (state) =>
    state.typeState.type === "Video" ? { ...state, typeState: update(state.typeState) } : state;

export const setVideoFileAction = (videoFile: File): StateAction<InvestorCommunicationEditState> =>
  updateVideoState((state) => ({ ...state, videoFile, uploadedVideoFileId: undefined, isVideoRemoved: false }));

export const removeVideoFileAction = (): StateAction<InvestorCommunicationEditState> =>
  updateVideoState((state) => ({
    ...state,
    videoFile: undefined,
    uploadedVideoFileId: undefined,
    isVideoRemoved: true,
  }));

export const setUploadedVideoFileIdAction = (
  uploadedVideoFileId: string
): StateAction<InvestorCommunicationEditState> => updateVideoState((state) => ({ ...state, uploadedVideoFileId }));

export const applyVideoCompletionErrorAction = (errorMessage: string): StateAction<InvestorCommunicationEditState> =>
  updateVideoState((state) =>
    errorMessage.startsWith(videoNoLongerAvailableError) ? { ...state, uploadedVideoFileId: undefined } : state
  );

export const setThumbnailFileAction = (thumbnailFile: File): StateAction<InvestorCommunicationEditState> =>
  updateVideoState((state) => ({ ...state, thumbnailFile, isThumbnailRemoved: false }));

export const removeThumbnailFileAction = (): StateAction<InvestorCommunicationEditState> =>
  updateVideoState((state) => ({ ...state, thumbnailFile: undefined, isThumbnailRemoved: true }));

export const setAllowDownloadAction = (allowDownload: boolean): StateAction<InvestorCommunicationEditState> =>
  updateVideoState((state) => ({ ...state, allowDownload }));
